import {useTranslation} from '@/translations/i18n.tsx'
import {ModalBody, ModalCloseButton, ModalHeader, ModalHeaderTitle, ModalTitle} from '@components/ui/modal-atoms'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'
import {FC} from 'react'
import {InputText} from '@components/commons/input-text'
import {AppleCompanyIcon, OutlookIcon} from '@components/ui/icon'
import {useCopyToClipboard} from '@hooks/useCopyToClipboard'
import {ButtonLink} from '@components/commons/button-link'
import {Button} from '@components/ui/button'

interface ConnectCalendarsModalProps {
    onClose: () => void
    iCalLink: string
    userId: number
}

export const ConnectCalendarsModal: FC<ConnectCalendarsModalProps> = ({onClose, iCalLink}) => {
    const {t} = useTranslation()
    const {copyToClipboard} = useCopyToClipboard()

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent
                className="w-110 grid grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden"
                showCloseButton={false}
            >
                <ModalHeader className="grid grid-cols-[32px_1fr_32px] items-center justify-center border-none">
                    <DialogClose data-slot="dialog-close" render={<ModalCloseButton />} />
                    <ModalHeaderTitle className="p-0">
                        {t('settings:personal_information:another_calendar_label')}
                    </ModalHeaderTitle>
                </ModalHeader>

                <ModalBody>
                    <div className="flex flex-col gap-4 items-start">
                        <ModalTitle>{t('settings:personal_information:another_calendar_title')}</ModalTitle>

                        <div className="flex flex-col gap-2.5 w-full items-start">
                            <p>{t('settings:personal_information:copy_link_label')}</p>
                            <div className="flex gap-5 w-full items-center ">
                                <InputText type="text" readOnly value={iCalLink} className="w-full" />
                                <Button
                                    className="whitespace-nowrap"
                                    onClick={() => copyToClipboard(iCalLink)}
                                    variant="tertiary"
                                    shape="text"
                                >
                                    {t('commons:copy')}
                                </Button>
                            </div>
                        </div>

                        <div className="flex flex-col gap-5 w-full items-start">
                            <p>{t('settings:personal_information:started_with_your_calendar')}</p>
                            <div className="flex flex-col gap-5 w-full items-start">
                                <ButtonLink
                                    to={'https://support.apple.com/guide/calendar/icl1022/mac'}
                                    variant="tertiary"
                                    target="_blank"
                                    shape="text"
                                >
                                    <AppleCompanyIcon />
                                    {t('settings:personal_information:apple_calendar_mac')}
                                </ButtonLink>
                                <ButtonLink
                                    to={'https://support.apple.com/guide/iphone/iph3d1110d4/ios'}
                                    variant="tertiary"
                                    target="_blank"
                                    shape="text"
                                >
                                    <AppleCompanyIcon />
                                    {t('settings:personal_information:apple_calendar_iphone')}
                                </ButtonLink>
                                <ButtonLink
                                    to="https://support.microsoft.com/office/import-or-subscribe-to-a-calendar-in-outlook-com-or-outlook-on-the-web-cff1429c-5af6-41ec-a5b4-74f2c278e98c"
                                    variant="tertiary"
                                    target="_blank"
                                    shape="text"
                                >
                                    <OutlookIcon />
                                    {t('settings:personal_information:microsoft_calendar')}
                                </ButtonLink>
                            </div>
                        </div>
                    </div>
                </ModalBody>
            </DialogContent>
        </Dialog>
    )
}
